import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Grid,
  GridItem,
  VStack,
  Button,
  Text,
  useToast,
  Flex,
  Skeleton,
  SkeletonText,
  SkeletonCircle,
  Spinner,
} from "@chakra-ui/react";
import DynamicFormContainer from "components/CommonForm/DynamicFormContainer";
import greek from "variables/greek.form.json";

import { ordersApiAction } from "stores/redux/apiSlices/orderApi/orderApiSlice";
import { productsApiAction } from "stores/redux/apiSlices/productApi/productApiSlice";
import { driversApiAction } from "stores/redux/apiSlices/driverApi/driverApiSlice";
import { clientsApiAction } from "stores/redux/apiSlices/clientApi/clientApiSlice";
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";
import "react-datepicker/dist/react-datepicker.css";

const AddOrder = ({ customerData, onOrderStatus }) => {
  console.log(customerData, "customerData");
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [productFieldsList, setProductFieldsList] = useState([
    { favouriteProducts: "", quantity: "", unitType: "" },
  ]);
  const isEdit = !!customerData?.orderId;

  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const clientFilters = useMemo(() => ({ role: "CLIENT", isActive: true }), []);
  const driverFilter = useMemo(() => ({ role: "DRIVER", isActive: true }), []);
  const filters = useMemo(() => ({ isActive: true }), []);

  const {
    data: clientData,
    isLoading: isClientsLoading,
    refetch: refetchClients,
  } = clientsApiAction.getClientListApi(clientFilters, {
    refetchOnMountOrArgChange: true,
  });
  const addProductFields = () => {
    setProductFieldsList((prev) => [
      ...prev,
      { favouriteProducts: "", quantity: "", unitType: "" },
    ]);
  };
  const {
    data: driverData,
    isLoading: isDriversLoading,
    refetch: refetchDriver,
  } = driversApiAction.getDriversListApi(driverFilter, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: productData,
    isLoading: isProductsLoading,
    refetch: refetchProducts,
  } = productsApiAction.getProductsListApi(filters, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetchClients();
    refetchDriver();
    refetchProducts();
  }, [refetchClients, refetchDriver, refetchProducts]);

  useEffect(() => {
    setLoadingDrivers(isDriversLoading);
  }, [isDriversLoading]);

  useEffect(() => {
    setLoadingProducts(isProductsLoading);
  }, [isProductsLoading]);

  const driverListData = driverData?.data?.users || [];
  const userListData = clientData?.data?.users || [];
  const productsListData = productData?.data?.products || [];

  const driverOptions = useMemo(() => {
    return driverListData.map((driver) => ({
      label: driver.name,
      value: driver.id,
    }));
  }, [driverListData]);

  const userOptions = useMemo(() => {
    return userListData.map((client) => ({
      label: client.name,
      value: client.id,
    }));
  }, [userListData]);

  const productOptions = useMemo(() => {
    return productsListData.map((product) => ({
      label: product.name,
      value: product.productId,
    }));
  }, [productsListData]);

  const dispatch = useDispatch();
  const [createOrderApi] = ordersApiAction.createOrdersApi();
  const [updateOrderApi] = ordersApiAction.updateOrdersApi();

  const [formData, setFormData] = useState({
    orderId: customerData?.orderId || "",
    orderDate: customerData?.orderedDate || "",
    deliveryDate: customerData?.deliveryDate || "",
    orderStatus: customerData?.orderStatus || "",
    clientName: customerData?.user.name || "",
    client: customerData?.user?.id || "",
    driver: customerData?.driver?.id || "",
    amount: customerData?.totalAmount || "",
    pylonCode: customerData?.user?.pylonCode || "",
    description: customerData?.remarks || "",
  });

  const [errors, setErrors] = useState({});
  const [toggleStates, setToggleStates] = useState({});
  useEffect(() => {
    if (formData.client && Array.isArray(userListData)) {
      const selectedClient = userListData.find(
        (client) => client.id === formData.client
      );

      if (selectedClient) {
        setFormData((prev) => ({
          ...prev,
          pylonCode: selectedClient.paylonCode || "", // ← fixed the typo here
          driver: selectedClient?.driver?.id || "",
        }));
      }
    }
  }, [formData.client, userListData]);

  useEffect(() => {
    if (customerData) {
      setFormData((prevData) => ({
        ...prevData,
        orderId: customerData.orderId || prevData.orderId,
        orderDate: customerData.orderedDate || prevData.orderDate,
        deliveryDate: customerData.deliveryDate || prevData.deliveryDate,
        orderStatus: customerData.orderStatus || prevData.orderStatus,
        clientName: customerData.user.name || prevData.clientName,
        driver: customerData?.driver?.id || prevData.driver.name,
        amount: customerData.totalAmount || prevData.amount,
        description: customerData?.remarks || prevData.description,
      }));
    }
  }, [customerData]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (customerData) {
      const newFormData = {
        orderId: customerData.orderId || "",
        orderDate: formatDate(customerData.orderedDate) || "",
        deliveryDate: customerData.deliveryDate || "",
        orderStatus: customerData.orderStatus || "",
        clientName: customerData.user?.name || "",
        driver: customerData?.driver?.id || "",
        amount: customerData.totalAmount || "",
        description: customerData.remarks || "",
      };

      if (
        Array.isArray(customerData.products) &&
        customerData.products.length > 0
      ) {
        customerData.products.forEach((prod, index) => {
          const productMatch = productsListData.find(
            (p) => p.name === prod.name || p.productId === prod.id
          );
          const matchedId = productMatch?.productId || prod.id;

          const selectedProduct = productsListData.find(
            (p) => p.productId === matchedId
          );
          const parsedQuantities =
            selectedProduct?.allowedQuantities?.split(",").map((q) => ({
              label: q.trim(),
              value: q.trim(),
            })) || [];

          newFormData[`favouriteProducts_${index}`] = matchedId;
          newFormData[`quantityValue_${index}`] = prod.qty;
          newFormData[`unitType_${index}`] =
            selectedProduct?.measurementType || "";
          newFormData[`quantity_${index}`] = parsedQuantities;
          newFormData[`quantityType_${index}`] =
            selectedProduct?.quantityType || "";
        });

        setProductFieldsList(
          new Array(customerData.products.length).fill(null)
        );
      }

      setFormData(newFormData);
    }
  }, [customerData, productsListData]);
  useEffect(() => {
    const updatedData = {};

    productFieldsList.forEach((_, index) => {
      const selectedProductId = formData[`favouriteProducts_${index}`];

      if (!selectedProductId) return;

      const selectedProduct = productsListData.find(
        (product) => product.productId === selectedProductId
      );

      if (!selectedProduct) return;

      const unitKey = `unitType_${index}`;
      const quantityKey = `quantity_${index}`;
      const quantityTypeKey = `quantityType_${index}`;

      const parsedQuantities =
        selectedProduct.allowedQuantities
          ?.split(",")
          .map((qty) => qty.trim())
          .map((qty) => ({
            label: qty,
            value: qty,
          })) || [];

      const shouldUpdate =
        formData[unitKey] !== selectedProduct.measurementType ||
        JSON.stringify(formData[quantityKey]) !==
          JSON.stringify(parsedQuantities) ||
        formData[quantityTypeKey] !== selectedProduct.quantityType;

      if (shouldUpdate) {
        updatedData[unitKey] = selectedProduct.measurementType || "";
        updatedData[quantityKey] = parsedQuantities;
        updatedData[quantityTypeKey] = selectedProduct.quantityType || "";
      }
    });

    if (Object.keys(updatedData).length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        ...updatedData,
      }));
    }
  }, [formData, productFieldsList, productsListData]);

  const products = productFieldsList.map((_, index) => {
    const selectedProduct = productsListData.find(
      (product) => product.productId === formData[`favouriteProducts_${index}`]
    );

    return {
      productId: formData[`favouriteProducts_${index}`],
      unitType: selectedProduct?.measurementType || "",
      quantity: Number(formData[`quantityValue_${index}`]) || 0,
    };
  });

  const handleRemoveRow = (indexToRemove) => {
    setProductFieldsList((prev) => {
      const updated = [...prev];
      updated.splice(indexToRemove, 1);
      return updated;
    });

    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData }; // clone all existing data

      // Remove old product-related keys
      productFieldsList.forEach((_, i) => {
        if (i === indexToRemove) {
          delete newFormData[`favouriteProducts_${i}`];
          delete newFormData[`quantityValue_${i}`];
          delete newFormData[`unitType_${i}`];
          delete newFormData[`quantity_${i}`];
          delete newFormData[`quantityType_${i}`];
        }
      });

      // Re-index the remaining product-related fields
      const productData = {};
      let newIndex = 0;

      productFieldsList.forEach((_, i) => {
        if (i === indexToRemove) return;

        productData[`favouriteProducts_${newIndex}`] =
          prevFormData[`favouriteProducts_${i}`];
        productData[`quantityValue_${newIndex}`] =
          prevFormData[`quantityValue_${i}`];
        productData[`unitType_${newIndex}`] = prevFormData[`unitType_${i}`];
        productData[`quantity_${newIndex}`] = prevFormData[`quantity_${i}`];
        productData[`quantityType_${newIndex}`] =
          prevFormData[`quantityType_${i}`];
        newIndex++;
      });

      return {
        ...newFormData,
        ...productData,
      };
    });
  };

  const validateForm = () => {
    let newErrors = {};

    if (
      Array.isArray(formConfig) &&
      formConfig.length > 0 &&
      Array.isArray(formConfig[0]?.fields)
    ) {
      formConfig[0].fields.forEach((field) => {
        if (field.required && !formData[field.name]) {
          newErrors[field.name] = "Αυτό το πεδίο είναι υποχρεωτικό";
        }
      });
    } else {
      console.warn("formConfig[0].fields is not available");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmitOnce = async (e) => {
    e?.preventDefault?.(); // In case inside a form

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await handleSubmit(); // main logic
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return Promise.resolve();

    if (!formData.orderId) {
      const createPayload = {
        userId: formData.client,
        driver: formData.driver,
        products,
        description: formData.description,
      };

      console.log("Creating Order with Payload:", createPayload);

      return createOrderApi(createPayload)
        .unwrap()
        .then((response) => {
          toast({
            title: "Success",
            description: response.message || "Οδηγός δημιουργήθηκε με επιτυχία",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          onOrderStatus?.(response.success);
        })
        .catch((error) => {
          toast({
            title: "Error",
            description: error.data?.message || "Αποτυχία δημιουργίας οδηγού",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        });
    } else {
      const updatePayload = {
        orderId: formData.orderId,
        driver: formData.driver,
        orderStatus: formData.orderStatus,
        deliveryDate: formData.deliveryDate,
        remarks: formData.description,
        products,
      };

      console.log("Updating Order with Payload:", updatePayload);

      return updateOrderApi(updatePayload)
        .unwrap()
        .then((response) => {
          toast({
            title: "Success",
            description: response.message || "Order Updated Successfully",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          onOrderStatus?.(response.success);
        })
        .catch((error) => {
          toast({
            title: "Error",
            description: error.data?.message || "Αποτυχία δημιουργίας οδηγού",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        });
    }
  };

  // const handleSubmit = () => {
  //   if (!validateForm()) {
  //     console.log("Validation failed");
  //     return;
  //   }

  //   if (!formData.orderId) {
  //     // **Create Order API Call**
  //     const createPayload = {
  //       userId: formData.client,
  //       driver: formData.driver,
  //       products,
  //       description: formData.description,

  //     };

  //     console.log("Creating Order with Payload:", createPayload);

  //     createOrderApi(createPayload)
  //       .unwrap()
  //       .then((response) => {
  //         toast({
  //           title: "Success",
  //           description: "Order Created Succesfully" || "Οδηγός δημιουργήθηκε με επιτυχία",
  //           status: "success",
  //           duration: 4000,
  //           isClosable: true,
  //         });
  //         onOrderStatus?.(response.success);
  //       })
  //       .catch((error) => {
  //         toast({
  //           title: "Error",
  //           description: error.data?.message || "Αποτυχία δημιουργίας οδηγού",
  //           status: "error",
  //           duration: 4000,
  //           isClosable: true,
  //         });
  //       });
  //   } else {
  //     // **Update Order API Call**
  //     const updatePayload = {
  //       orderId: formData.orderId,
  //       driver: formData.driver,
  //       orderStatus: formData.orderStatus,
  //       deliveryDate: formData.deliveryDate,
  //       remarks: formData.description,
  //       products,
  //     };

  //     console.log("Updating Order with Payload:", updatePayload);

  //     updateOrderApi(updatePayload)
  //       .unwrap()
  //       .then((response) => {
  //         toast({
  //           title: "Success",
  //           description: "Order Updated Succesfully" || "Οδηγός δημιουργήθηκε με επιτυχία",
  //           status: "success",
  //           duration: 4000,
  //           isClosable: true,
  //         });
  //         onOrderStatus?.(response.success);
  //       })
  //       .catch((error) => {
  //         toast({
  //           title: "Error",
  //           description: error.data?.message || "Αποτυχία δημιουργίας οδηγού",
  //           status: "error",
  //           duration: 4000,
  //           isClosable: true,
  //         });
  //       });
  //   }
  // };

  const isEditMode = !!customerData?.orderId;

  // Greek labels and placeholders loaded from Greek locale JSON

  const formConfig = [
    {
      id: "orderInfo",
      title: greek.form.orderInfoTitle,
      fields: isEditMode
        ? [
            {
              name: "orderId",
              label: greek.form.orderId.label,
              placeholder: greek.form.orderId.placeholder,
              type: "text",
              gridSize: 6,
              editable: false,
            },
            {
              name: "orderDate",
              label: greek.form.orderDate.label,
              placeholder: greek.form.orderDate.placeholder,
              type: "text",
              gridSize: 6,
              editable: false,
            },
            {
              name: "deliveryDate",
              label: greek.form.deliveryDate.label,
              placeholder: greek.form.deliveryDate.placeholder,
              type: "date",
              gridSize: 6,
            },
            {
              name: "orderStatus",
              label: greek.form.orderStatus.label,
              placeholder: greek.form.orderStatus.placeholder,
              type: "select",
              options: [
                { label: "Νέα", value: "NEW" },
                { label: "Ολοκληρωμένη", value: "COMPLETED" },
                { label: "Ακυρωμένη", value: "CANCELLED" },
              ],
              required: true,
              gridSize: 6,
              editable: false,
            },
            {
              name: "clientName",
              label: greek.form.clientName.label,
              placeholder: greek.form.clientName.placeholder,
              type: "text",
              required: true,
              gridSize: 6,
              editable: false,
            },
            {
              name: "driver",
              label: greek.form.assignedDriver.label,
              placeholder: greek.form.assignedDriver.placeholder,
              type: "select",
              options: driverOptions,
              gridSize: 6,
              required: true,
            },
            {
              name: "description",
              label: greek.form.description.label,
              placeholder: greek.form.description.placeholder,
              type: "text",
              gridSize: 12,
            },
          ]
        : [
            {
              name: "client",
              label: greek.form.clientName.label,
              placeholder: greek.form.clientName.placeholder,
              type: "select",
              options: userOptions,
              gridSize: 6,
              required: true,
            },
            {
              name: "driver",
              label: greek.form.assignedDriver.label,
              placeholder: greek.form.assignedDriver.placeholder,
              type: "select",
              options: driverOptions,
              gridSize: 6,
              required: true,
            },
            {
              name: "pylonCode",
              label: "Κωδικός PYLON",
              placeholder: "",
              type: "number",
              gridSize: 6,
              editable: false,
              required: true,
            },
            {
              name: "description",
              label: greek.form.description.label,
              placeholder: greek.form.description.placeholder,
              type: "text",
              gridSize: 12,
            },
          ],
    },
  ];
  const unitTypeToGreek = {
    BOXES: "ΚΟΥΤΕΣ",
    PIECES: "ΤΜΧ",
    KGS: "ΚΙΛΑ",
  };

  const productConfig = [
    {
      id: "orderInfo",
      title: "Προϊόντα στην παραγγελία",
      fields: productFieldsList
        .map((_, index) => {
          const isLastItem = index === productFieldsList.length - 1;
          const isOnlyItem = productFieldsList.length === 1;

          const quantityType = formData[`quantityType_${index}`];
          const quantityOptions = formData[`quantity_${index}`] || [];
          const quantityValue = formData[`quantityValue_${index}`];

          const quantityField =
            quantityType === "FIXED"
              ? {
                  name: `quantityValue_${index}`,
                  label: index === 0 ? greek.form.quantity.label : "",
                  placeholder: greek.form.quantity.placeholder,
                  type: "select",
                  options: quantityOptions,
                  gridSize: 3,
                  value: quantityValue?.toString(),
                }
              : {
                  name: `quantityValue_${index}`,
                  label: index === 0 ? greek.form.quantity.label : "",
                  placeholder: greek.form.quantity.placeholder,
                  type: "number",
                  gridSize: 3,
                  value: quantityValue,
                };

          const baseFields = [
            {
              name: `favouriteProducts_${index}`,
              label: index === 0 ? greek.form.products.label : "",
              placeholder: greek.form.products.placeholder,
              type: "select",
              options: productOptions,
              required: index === 0,
              gridSize: 3,
            },
            quantityField,
            {
              name: `unitType_${index}`,
              label: index === 0 ? greek.form.unitType.label : "",
              placeholder: greek.form.unitType.placeholder,
              type: "text",
              gridSize: 4,
              editable: false,
              displayValue:
                unitTypeToGreek[formData[`unitType_${index}`]] ||
                formData[`unitType_${index}`],
            },
          ];

          if (isOnlyItem) {
            baseFields.push({
              name: `addRow_${index}`,
              type: "icon-button",
              icon: <FaPlusCircle color="green" />,
              gridSize: 1,
              onIconClick: () => addProductFields(),
              customStyle: {
                mt: "25px",
              },
            });
          } else if (isLastItem) {
            baseFields.push(
              {
                name: `removeRow_${index}`,
                type: "icon-button",
                icon: <FaMinusCircle color="red.400" />,
                gridSize: 1,
                onIconClick: () => handleRemoveRow(index),
                customStyle: index === 0 ? { mt: "25px" } : {},
              },
              {
                name: `addRow_${index}`,
                type: "icon-button",
                icon: <FaPlusCircle color="green" />,
                gridSize: 1,
                onIconClick: () => addProductFields(),
                customStyle: {},
              }
            );
          } else {
            baseFields.push({
              name: `removeRow_${index}`,
              type: "icon-button",
              icon: <FaMinusCircle color="red.400" />,
              gridSize: 1,
              onIconClick: () => handleRemoveRow(index),
              customStyle: index === 0 ? { mt: "25px" } : {},
            });
          }

          return baseFields;
        })
        .flat(),
    },
  ];

  const isLoadingAll = loadingDrivers || loadingProducts || isClientsLoading;

  return (
    <Box mt={24}>
      {isLoadingAll ? (
        // Skeleton Loader
        <Grid
          mt={4}
          templateColumns="repeat(2, 1fr)"
          gap={6}
          alignItems="start"
        >
          <GridItem>
            <VStack spacing={6} align="stretch">
              <Skeleton height="420px" borderRadius="md" />
            </VStack>
          </GridItem>
          <GridItem>
            <VStack spacing={6} align="stretch">
              <Skeleton height="420px" borderRadius="md" />
            </VStack>
          </GridItem>
        </Grid>
      ) : (
        // Actual form UI
        <Grid mt={4} templateColumns="55% 45%" gap={6} alignItems="start">
          <GridItem>
            <VStack spacing={6} align="stretch">
              <DynamicFormContainer
                key={productConfig[0].id}
                title={productConfig[0].title}
                cardData={productConfig[0]}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                minHeight="480px"
                maxHeight="480px"
                // renderExtra={
                //   <Box position="absolute" top="450px" left="50px">
                //     <Button
                //       onClick={addProductFields}
                //       size="sm"
                //       borderRadius="full"
                //       width="32px"
                //       height="32px"
                //       fontSize="20px"
                //       p="0"
                //       bg="blue.500"
                //       color="white"
                //       _hover={{ bg: "blue.600" }}
                //       _active={{ bg: "blue.700" }}
                //     >
                //       +
                //     </Button>
                //   </Box>
                // }
              />
            </VStack>
          </GridItem>
          <GridItem>
            <VStack spacing={6} align="stretch">
              <DynamicFormContainer
                key={formConfig[0].id}
                title={formConfig[0].title}
                cardData={formConfig[0]}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                minHeight="420px"
                maxHeight="420px"
              />
            </VStack>
          </GridItem>
        </Grid>
      )}

      {Object.keys(errors).length > 0 && (
        <Text color="red.500" mt={4}>
          Παρακαλώ διορθώστε τα σφάλματα πριν την υποβολή.
        </Text>
      )}

      <Flex justifyContent="flex-end">
        <Button
          mt={0}
          bg="linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)"
          _hover={{
            bg: "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
          }}
          _disabled={{
            _hover: {
              bg: "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
            },
            opacity: 0.6,
            cursor: "not-allowed",
          }}
          color="#fff"
          minW="125px"
          h="35px"
          fontSize="xs"
          fontWeight="bold"
          mb={{ sm: "12px", md: "0px" }}
          onClick={handleSubmitOnce}
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {isSubmitting ? (
            <Spinner size="sm" color="white" />
          ) : isEdit ? (
            "Ενημέρωση Παραγγελίας"
          ) : (
            "Προσθήκη Παραγγελίας"
          )}
        </Button>
      </Flex>
    </Box>
  );
};

export default AddOrder;
